/**
 * seatRoutePolicy — unified role → route policy map for all workspace seats.
 *
 * Replaces the attorney-only deny-list with one policy per role.
 * Every policy is a deny-list (blockedPrefixes) so unmapped routes stay open
 * by default — we block explicitly, never lock out by omission.

 *
 * `/attorney/*` and `/workdesk/attorney` are intentionally NOT listed for staff
 * seats: those routes sit behind ProtectedAttorneyRoute (not ProtectedRoute), which
 * deliberately admits company staff for cross-firm oversight
 * (AttorneyAuthContext.tsx), so SeatRouteGuard never runs there. Changing that is an
 * owner decision, not a map edit.
 */

import type { WorkspaceRole } from '../../config/toolsManifest';

export type SeatRole = WorkspaceRole;

export interface SeatPolicy {
  home: string;
  blockedPrefixes: string[];
  allowExceptions?: string[];
}

export const SEAT_ROUTE_POLICY: Record<SeatRole, SeatPolicy> = {
  attorney: {
    home: '/attorney/workdesk',
    blockedPrefixes: ['/dashboard', '/queue', '/adjuster', '/data-specialist'],
    allowExceptions: ['/queue/attorney'],
  },
  data_specialist: {
    home: '/workdesk/data-specialist',
    blockedPrefixes: [
      '/dashboard/accountant',
      '/workdesk/accountant-internal',
      '/workdesk/accountant-external',
      '/adjuster',
    ],
  },
  accountant_internal: {
    home: '/workdesk/accountant-internal',
    blockedPrefixes: [
      '/adjuster',
      '/data-specialist',
      '/workdesk/data-specialist',
    ],
  },
  admin: { home: '/workdesk/admin', blockedPrefixes: [] },
  analyst: { home: '/workdesk/analyst', blockedPrefixes: [] },
  attorney_internal: { home: '/attorney/workdesk', blockedPrefixes: [] },
  mediator: { home: '/mediation-wall', blockedPrefixes: [] },
  client: { home: '/client', blockedPrefixes: [] },
  accountant_external: { home: '/workdesk/accountant-external', blockedPrefixes: [] },
};

/**
 * Return true when `pathname` is blocked for the given seat role.
 *
 * Matching is segment-exact: `/dashboardx` is NOT blocked by `/dashboard`.
 * Query strings and trailing slashes are ignored.
 */
export function isPathBlocked(role: SeatRole, pathname: string): boolean {
  const policy = SEAT_ROUTE_POLICY[role];
  if (!policy || policy.blockedPrefixes.length === 0) {
    return false;
  }

  const path = pathname.split('?')[0];
  const normalized =
    path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

  for (const prefix of policy.blockedPrefixes) {
    if (normalized === prefix || normalized.startsWith(prefix + '/')) {
      if (policy.allowExceptions) {
        for (const exc of policy.allowExceptions) {
          if (normalized === exc || normalized.startsWith(exc + '/')) {
            return false;
          }
        }
      }
      return true;
    }
  }
  return false;
}

/**
 * Backward-compatible wrapper — the old attorney-only guard.
 * Existing imports and tests continue to work unchanged.
 */
export function isStaffOnlyPathForAttorney(pathname: string): boolean {
  return isPathBlocked('attorney', pathname);
}
